import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Plus, Trash2, DollarSign, Lock, BarChart3 } from "lucide-react";
import { getProducts, computeProductLine } from "../lib/products";
import { formatCurrency } from "../lib/storage";
import ProBadge from "../components/ProBadge";
import UpgradeButton from "../components/UpgradeButton";

/**
 * Section embedded in the Planner event form. Lets a Pro user attach products
 * from their library to this event, with per-event qty/price/notes.
 *
 * Props:
 *   value: array of event-product entries  [{productId, qtyBrought, qtySold, salePriceOverride, notes}]
 *   onChange: (nextArray) => void
 *   pro: boolean
 */
export default function EventProductsSection({ value = [], onChange, pro }) {
  const allProducts = useMemo(() => getProducts(), []);
  const lookup = useMemo(() => Object.fromEntries(allProducts.map((p) => [p.id, p])), [allProducts]);
  const [selectId, setSelectId] = useState("");

  const usedIds = new Set(value.map((e) => e.productId));
  const available = allProducts.filter((p) => !usedIds.has(p.id));

  const summary = useMemo(() => {
    let rev = 0, cost = 0;
    value.forEach((ep) => {
      const line = computeProductLine(ep, lookup[ep.productId]);
      rev += line.revenue;
      cost += line.cost;
    });
    return { revenue: rev, cost, profit: rev - cost };
  }, [value, lookup]);

  if (!pro) {
    return (
      <section data-testid="event-products-paywall">
        <SectionHeader />
        <div className="pop-card p-5 bg-brand-gold/5 border-brand-gold/30 flex items-start gap-3">
          <span className="w-10 h-10 rounded-xl bg-brand-gold/15 text-brand-gold grid place-items-center shrink-0">
            <Lock className="w-5 h-5" strokeWidth={2} />
          </span>
          <div className="flex-1">
            <p className="font-heading text-base font-semibold text-brand-ink">Track products per event</p>
            <p className="text-xs text-brand-mute mt-1">
              Assign your saved products to this event, track inventory brought vs. sold, and see which products drive the most profit.
            </p>
            <div className="mt-3">
              <UpgradeButton variant="ghost" label="Unlock — $9/month" testid="event-products-upgrade-btn" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  const addProduct = () => {
    if (!selectId) return;
    const next = [
      ...value,
      { productId: selectId, qtyBrought: "", qtySold: "", salePriceOverride: "", notes: "" },
    ];
    onChange(next);
    setSelectId("");
  };

  const update = (idx, patch) => {
    const next = value.map((ep, i) => (i === idx ? { ...ep, ...patch } : ep));
    onChange(next);
  };

  const remove = (idx) => {
    const next = value.filter((_, i) => i !== idx);
    onChange(next);
  };

  return (
    <section data-testid="event-products-section">
      <SectionHeader />

      {allProducts.length === 0 ? (
        <div className="pop-card p-5 flex items-start gap-3" data-testid="event-products-empty-library">
          <span className="w-10 h-10 rounded-xl bg-brand-cream-soft text-brand-mute grid place-items-center shrink-0">
            <Package className="w-5 h-5" strokeWidth={2} />
          </span>
          <div className="flex-1">
            <p className="text-sm font-medium text-brand-ink">No products in your library yet.</p>
            <p className="text-xs text-brand-mute mt-1">
              Add your products once on the Products page, then assign them to any event.
            </p>
            <Link to="/products" className="pop-btn-secondary mt-3 inline-flex" data-testid="event-products-open-library">
              <Plus className="w-4 h-4" /> Open Product Library
            </Link>
          </div>
        </div>
      ) : (
        <>
          {/* Selector */}
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <select
              value={selectId}
              onChange={(e) => setSelectId(e.target.value)}
              className="pop-input flex-1"
              data-testid="event-products-select"
              disabled={available.length === 0}
            >
              <option value="">
                {available.length === 0 ? "All products added" : "Choose a product to add…"}
              </option>
              {available.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}{p.category ? ` — ${p.category}` : ""}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={addProduct}
              disabled={!selectId}
              className="pop-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="event-products-add"
            >
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>

          {/* Rows */}
          {value.length === 0 ? (
            <p className="text-xs text-brand-mute italic">No products attached to this event yet.</p>
          ) : (
            <div className="space-y-3" data-testid="event-products-rows">
              {value.map((ep, idx) => {
                const product = lookup[ep.productId];
                const line = computeProductLine(ep, product);
                if (!product) {
                  return (
                    <div key={idx} className="pop-card p-4 flex items-center justify-between gap-3 bg-brand-error/5 border-brand-error/30">
                      <p className="text-sm text-brand-error">Product deleted from library</p>
                      <button type="button" onClick={() => remove(idx)} className="text-brand-error" data-testid={`event-products-remove-${ep.productId || `idx-${idx}`}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  );
                }
                return (
                  <div key={ep.productId} className="pop-card p-4" data-testid={`event-products-row-${ep.productId}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-heading text-base font-semibold text-brand-ink truncate">{product.name}</p>
                        <p className="text-[11px] text-brand-mute mt-0.5">
                          {product.category && <>{product.category} · </>}
                          Cost {formatCurrency(+product.costPerItem || 0)} · Default {formatCurrency(+product.defaultSalePrice || 0)}
                        </p>
                      </div>
                      <button type="button" onClick={() => remove(idx)} className="w-8 h-8 grid place-items-center rounded-full hover:bg-brand-error/10 text-brand-mute hover:text-brand-error transition-colors" aria-label="Remove" data-testid={`event-products-remove-${ep.productId}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <NumField
                        id={`evp-qtybrought-${ep.productId}`}
                        label="Qty brought"
                        value={ep.qtyBrought}
                        onChange={(v) => update(idx, { qtyBrought: v })}
                      />
                      <NumField
                        id={`evp-qtysold-${ep.productId}`}
                        label="Qty sold"
                        value={ep.qtySold}
                        onChange={(v) => update(idx, { qtySold: v })}
                      />
                      <PriceField
                        id={`evp-price-${ep.productId}`}
                        label="Sale price"
                        placeholder={(+product.defaultSalePrice || 0).toFixed(2)}
                        value={ep.salePriceOverride}
                        onChange={(v) => update(idx, { salePriceOverride: v })}
                      />
                    </div>

                    <input
                      type="text"
                      placeholder="Notes (optional)"
                      value={ep.notes || ""}
                      onChange={(e) => update(idx, { notes: e.target.value })}
                      className="pop-input mt-3 text-sm"
                      data-testid={`evp-notes-${ep.productId}`}
                    />

                    {/* Mini stats */}
                    <div className="mt-3 grid grid-cols-3 gap-2 text-[11px]">
                      <MiniStat label="Revenue" value={formatCurrency(line.revenue)} />
                      <MiniStat label="Cost" value={formatCurrency(line.cost)} />
                      <MiniStat label="Profit" value={formatCurrency(line.profit)} good={line.profit > 0} bad={line.profit < 0} />
                    </div>
                  </div>
                );
              })}

              {/* Section subtotal */}
              <div className="rounded-xl bg-brand-green/5 border border-brand-green/15 p-3 flex items-center justify-between gap-3" data-testid="event-products-subtotal">
                <div className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-brand-green" strokeWidth={2} />
                  <p className="text-xs font-medium text-brand-ink">{value.length} product{value.length === 1 ? "" : "s"} on this event</p>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <span><span className="text-brand-mute">Revenue</span> <span className="font-semibold text-brand-ink tabular-nums">{formatCurrency(summary.revenue)}</span></span>
                  <span><span className="text-brand-mute">Profit</span> <span className={`font-semibold tabular-nums ${summary.profit > 0 ? "text-brand-green" : summary.profit < 0 ? "text-brand-error" : "text-brand-ink"}`}>{formatCurrency(summary.profit)}</span></span>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function SectionHeader() {
  return (
    <div className="flex items-center gap-2.5 mb-3">
      <span className="w-8 h-8 rounded-lg bg-brand-green/10 text-brand-green grid place-items-center">
        <Package className="w-4 h-4" strokeWidth={2} />
      </span>
      <div>
        <h4 className="font-heading text-sm font-semibold text-brand-ink uppercase tracking-wider flex items-center gap-2">
          Products on this event <ProBadge />
        </h4>
        <p className="text-xs text-brand-mute mt-0.5">
          Pull from your <Link to="/products" className="text-brand-green hover:underline">Product Library</Link>. Track what you brought vs. what sold.
        </p>
      </div>
    </div>
  );
}

function NumField({ id, label, value, onChange }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider font-semibold text-brand-mute mb-1" htmlFor={id}>{label}</label>
      <input
        id={id}
        data-testid={id}
        type="number"
        inputMode="numeric"
        min="0"
        placeholder="0"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="pop-input text-base"
      />
    </div>
  );
}

function PriceField({ id, label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-wider font-semibold text-brand-mute mb-1" htmlFor={id}>{label}</label>
      <div className="relative">
        <DollarSign className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-mute pointer-events-none" />
        <input
          id={id}
          data-testid={id}
          type="number"
          inputMode="decimal"
          min="0"
          step="0.01"
          placeholder={placeholder || "0.00"}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="pop-input pl-11 text-base"
        />
      </div>
    </div>
  );
}

function MiniStat({ label, value, good = false, bad = false }) {
  return (
    <div className="rounded-lg bg-brand-cream-soft p-2">
      <p className="text-[9px] uppercase tracking-wider font-semibold text-brand-mute">{label}</p>
      <p className={`font-heading text-sm font-semibold mt-0.5 tabular-nums ${good ? "text-brand-green" : bad ? "text-brand-error" : "text-brand-ink"}`}>{value}</p>
    </div>
  );
}
